import {
  LogicalOperator,
  SearchOperator,
  type SearchCriteria,
  type SearchQueryRequest,
} from "@/openapi/generated";

/** Season criteria - supported by BE search (`Season` Equals). */
export const STUDENT_SEASON_FILTER_ENABLED = true;

/** Location criteria - supported by BE search (`Location` Equals / In). */
export const STUDENT_LOCATION_FILTER_ENABLED = true;

/** Age range criteria - supported by BE search (`AgeMin` / `AgeMax`). */
export const STUDENT_AGE_FILTER_ENABLED = true;

/** Enrolled class count - supported by BE search (`ClassCount` Equals). */
export const STUDENT_CLASS_COUNT_FILTER_ENABLED = true;

/** Has enrollments - supported by BE search (`HasClasses` Equals). */
export const STUDENT_HAS_CLASSES_FILTER_ENABLED = true;

/** Recital enrollment - supported by BE search (`Recital` Equals). */
export const STUDENT_RECITAL_FILTER_ENABLED = true;

/** Outstanding balance - supported by BE search (`HasBalance` Equals). */
export const STUDENT_BALANCE_FILTER_ENABLED = true;

export interface StudentFiltersState {
  firstName: string;
  lastName: string;
  email: string;
  cellPhone: string;
  /** ISO date YYYY-MM-DD for DateOfBirth Equals criterion. */
  dateOfBirth: string;
  active: boolean | null;
  seasonId: number | null;
  locationIds: number[];
  ageMin: number | null;
  ageMax: number | null;
  classCount: number | null;
  hasClasses: boolean | null;
  /** null = all, true = in recital, false = not in recital */
  recital: boolean | null;
  hasBalance: boolean | null;
}

export const EMPTY_STUDENT_FILTERS: StudentFiltersState = {
  firstName: "",
  lastName: "",
  email: "",
  cellPhone: "",
  dateOfBirth: "",
  active: null,
  seasonId: null,
  locationIds: [],
  ageMin: null,
  ageMax: null,
  classCount: null,
  hasClasses: null,
  recital: null,
  hasBalance: null,
};

function idsEqual(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort((x, y) => x - y);
  const sortedB = [...b].sort((x, y) => x - y);
  return sortedA.every((id, index) => id === sortedB[index]);
}

export function studentFiltersEqual(
  a: StudentFiltersState,
  b: StudentFiltersState,
): boolean {
  return (
    a.firstName === b.firstName &&
    a.lastName === b.lastName &&
    a.email === b.email &&
    a.cellPhone === b.cellPhone &&
    a.dateOfBirth === b.dateOfBirth &&
    a.active === b.active &&
    a.seasonId === b.seasonId &&
    idsEqual(a.locationIds, b.locationIds) &&
    a.ageMin === b.ageMin &&
    a.ageMax === b.ageMax &&
    a.classCount === b.classCount &&
    a.hasClasses === b.hasClasses &&
    a.recital === b.recital &&
    a.hasBalance === b.hasBalance
  );
}

export function countStudentFilters(filters: StudentFiltersState): number {
  let count = 0;
  if (filters.firstName.trim()) count++;
  if (filters.lastName.trim()) count++;
  if (filters.email.trim()) count++;
  if (filters.cellPhone.trim()) count++;
  if (filters.dateOfBirth.trim()) count++;
  if (filters.active !== null) count++;
  if (STUDENT_SEASON_FILTER_ENABLED && filters.seasonId !== null) count++;
  if (STUDENT_LOCATION_FILTER_ENABLED && filters.locationIds.length > 0) {
    count++;
  }
  if (
    STUDENT_AGE_FILTER_ENABLED &&
    (filters.ageMin !== null || filters.ageMax !== null)
  ) {
    count++;
  }
  if (STUDENT_CLASS_COUNT_FILTER_ENABLED && filters.classCount !== null) {
    count++;
  }
  if (STUDENT_HAS_CLASSES_FILTER_ENABLED && filters.hasClasses !== null) {
    count++;
  }
  if (STUDENT_RECITAL_FILTER_ENABLED && filters.recital !== null) {
    count++;
  }
  if (STUDENT_BALANCE_FILTER_ENABLED && filters.hasBalance !== null) {
    count++;
  }
  return count;
}

/** Modal badge count - excludes names owned by quick search. */
export function countStudentModalFilters(
  filters: StudentFiltersState,
  quickFirstName = "",
  quickLastName = "",
): number {
  let count = countStudentFilters(filters);
  if (quickFirstName.trim() && filters.firstName.trim()) {
    count -= 1;
  }
  if (quickLastName.trim() && filters.lastName.trim()) {
    count -= 1;
  }
  return count;
}

export function countActiveStudentCriteria(
  quickFirstName: string,
  filters: StudentFiltersState,
  quickLastName = "",
): number {
  let count = countStudentFilters(filters);
  if (quickFirstName.trim() && !filters.firstName.trim()) {
    count += 1;
  }
  if (quickLastName.trim() && !filters.lastName.trim()) {
    count += 1;
  }
  return count;
}

export function hasActiveStudentFilters(filters: StudentFiltersState): boolean {
  return countStudentFilters(filters) > 0;
}

export function hasActiveStudentSearch(
  quickFirstName: string,
  filters: StudentFiltersState,
  quickLastName = "",
): boolean {
  return (
    quickFirstName.trim() !== "" ||
    quickLastName.trim() !== "" ||
    hasActiveStudentFilters(filters)
  );
}

function appendCriterion(
  criteria: SearchCriteria[],
  field: string,
  operator: SearchOperator,
  value: string | number | boolean | number[],
): void {
  criteria.push({
    field,
    operator,
    value,
    logicalOperator:
      criteria.length > 0 ? LogicalOperator.And : undefined,
  });
}

export function buildStudentSearchQuery(
  quickFirstName: string,
  filters: StudentFiltersState,
  quickLastName = "",
): SearchQueryRequest | null {
  const criteria: SearchCriteria[] = [];

  const firstName = quickFirstName.trim() || filters.firstName.trim();
  if (firstName) {
    appendCriterion(criteria, "FirstName", SearchOperator.Contains, firstName);
  }

  const lastName = quickLastName.trim() || filters.lastName.trim();
  if (lastName) {
    appendCriterion(criteria, "LastName", SearchOperator.Contains, lastName);
  }

  const email = filters.email.trim();
  if (email) {
    appendCriterion(criteria, "Email", SearchOperator.Contains, email);
  }

  const cellPhone = filters.cellPhone.trim();
  if (cellPhone) {
    appendCriterion(criteria, "CellPhone", SearchOperator.Contains, cellPhone);
  }

  const dateOfBirth = filters.dateOfBirth.trim();
  if (dateOfBirth) {
    appendCriterion(
      criteria,
      "DateOfBirth",
      SearchOperator.Equals,
      dateOfBirth,
    );
  }

  if (STUDENT_SEASON_FILTER_ENABLED && filters.seasonId !== null) {
    appendCriterion(
      criteria,
      "Season",
      SearchOperator.Equals,
      filters.seasonId,
    );
  }

  if (STUDENT_LOCATION_FILTER_ENABLED && filters.locationIds.length === 1) {
    appendCriterion(
      criteria,
      "Location",
      SearchOperator.Equals,
      filters.locationIds[0],
    );
  } else if (
    STUDENT_LOCATION_FILTER_ENABLED &&
    filters.locationIds.length > 1
  ) {
    appendCriterion(
      criteria,
      "Location",
      SearchOperator.In,
      filters.locationIds,
    );
  }

  if (STUDENT_AGE_FILTER_ENABLED && filters.ageMin !== null) {
    appendCriterion(
      criteria,
      "AgeMin",
      SearchOperator.GreaterThanOrEqual,
      filters.ageMin,
    );
  }

  if (STUDENT_AGE_FILTER_ENABLED && filters.ageMax !== null) {
    appendCriterion(
      criteria,
      "AgeMax",
      SearchOperator.LessThanOrEqual,
      filters.ageMax,
    );
  }

  if (STUDENT_CLASS_COUNT_FILTER_ENABLED && filters.classCount !== null) {
    appendCriterion(
      criteria,
      "ClassCount",
      SearchOperator.Equals,
      filters.classCount,
    );
  }

  if (STUDENT_HAS_CLASSES_FILTER_ENABLED && filters.hasClasses !== null) {
    appendCriterion(
      criteria,
      "HasClasses",
      SearchOperator.Equals,
      filters.hasClasses,
    );
  }

  if (STUDENT_RECITAL_FILTER_ENABLED && filters.recital !== null) {
    appendCriterion(
      criteria,
      "Recital",
      SearchOperator.Equals,
      filters.recital,
    );
  }

  if (STUDENT_BALANCE_FILTER_ENABLED && filters.hasBalance !== null) {
    appendCriterion(
      criteria,
      "HasBalance",
      SearchOperator.Equals,
      filters.hasBalance,
    );
  }

  if (criteria.length === 0) return null;
  return { criteria };
}

export function toStudentSearchBody(
  searchQueryRequest: SearchQueryRequest | null | undefined,
): SearchQueryRequest | undefined {
  const criteria = searchQueryRequest?.criteria;
  if (criteria && criteria.length > 0) {
    return searchQueryRequest;
  }
  return undefined;
}

export const STUDENT_AGE_OPTIONS = Array.from({ length: 100 }, (_, i) => ({
  label: String(i),
  value: String(i),
}));

export const STUDENT_CLASS_COUNT_OPTIONS = Array.from(
  { length: 15 },
  (_, i) => ({
    label: String(i + 1),
    value: String(i + 1),
  }),
);

export type StudentFilterChipKey =
  | keyof StudentFiltersState
  | "locationIds"
  | "quickFirstName"
  | "quickLastName";

export function formatStudentsListDescription(
  totalCount: number,
  quickFirstName: string,
  filters: StudentFiltersState,
  quickLastName = "",
): string {
  const matchWord = totalCount === 1 ? "student" : "students";
  const criteriaCount = countActiveStudentCriteria(
    quickFirstName,
    filters,
    quickLastName,
  );
  const quickFirst = quickFirstName.trim();
  const quickLast = quickLastName.trim();

  if (criteriaCount === 0) {
    return `${totalCount} ${matchWord}`;
  }

  if (quickFirst && quickLast && criteriaCount === 2) {
    return `${totalCount} ${matchWord} with first name “${quickFirst}” and last name “${quickLast}”`;
  }

  if (quickFirst && criteriaCount === 1) {
    return `${totalCount} ${matchWord} with first name “${quickFirst}”`;
  }

  if (quickLast && criteriaCount === 1) {
    return `${totalCount} ${matchWord} with last name “${quickLast}”`;
  }

  if (quickFirst) {
    return `${totalCount} ${matchWord} matching “${quickFirst}” and ${criteriaCount - 1} other ${
      criteriaCount - 1 === 1 ? "criterion" : "criteria"
    }`;
  }

  if (quickLast) {
    return `${totalCount} ${matchWord} matching last name “${quickLast}” and ${criteriaCount - 1} other ${
      criteriaCount - 1 === 1 ? "criterion" : "criteria"
    }`;
  }

  return `${totalCount} ${matchWord} matching ${criteriaCount} search ${
    criteriaCount === 1 ? "criterion" : "criteria"
  }`;
}
